/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    TextField,
    Grid,
    Paper,
    Chip,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Slider,
    Divider,
    IconButton,
    Collapse,
    CircularProgress,
    Alert,
    Skeleton,
    useMediaQuery,
    useTheme,
    Drawer,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Breadcrumbs,
    Link,
    Badge,
    RadioGroup,
    Radio,
    Popover,
} from '@mui/material';
import {
    Flight,
    FlightTakeoff,
    FlightLand,
    LocationOn,
    CalendarToday,
    Person,
    Search,
    FilterList,
    ExpandMore,
    ExpandLess,
    CheckCircle,
    Close,
    TuneRounded,
    ArrowBack,
    Home,
    Add,
    Remove,
    AccessTime,
    AirlineSeatReclineExtra,
    SwapHoriz,
    ArrowRightAlt,
    ChildCare,
    ChildFriendly,
    Luggage,
    Restaurant,
    Wifi,
    Power,
    EventSeat,
} from '@mui/icons-material';
import { todayLocalString, formatDate } from '../utils/dateHelpers';

// ============================================================================
// TYPES
// ============================================================================

interface FlightResult {
    id: string;
    airline: string;
    airlineCode: string;
    airlineLogo: string;
    flightNumber: string;
    origin: string;
    originCode: string;
    destination: string;
    destinationCode: string;
    departureTime: string;
    arrivalTime: string;
    duration: string;
    durationMinutes: number;
    stops: number;
    stopCities?: string[];
    price: number;
    currency: string;
    cabinClass: string;
    seatsLeft?: number;
    aircraft: string;
    amenities: string[];
    baggageIncluded: boolean;
    refundable: boolean;
    // For round trips
    returnFlight?: {
        flightNumber: string;
        departureTime: string;
        arrivalTime: string;
        duration: string;
        stops: number;
    };
}

interface SearchFilters {
    priceRange: [number, number];
    stops: number[];
    airlines: string[];
    departureTime: string[];
    arrivalTime: string[];
    refundable: boolean;
    baggageIncluded: boolean;
}

type SortOption = 'best' | 'price_low' | 'price_high' | 'duration' | 'departure' | 'arrival';

// ============================================================================
// CONSTANTS
// ============================================================================

const AIRLINES = [
    { code: 'UA', name: 'United Airlines' },
    { code: 'AA', name: 'American Airlines' },
    { code: 'DL', name: 'Delta Air Lines' },
    { code: 'SW', name: 'Southwest Airlines' },
    { code: 'B6', name: 'JetBlue Airways' },
    { code: 'AS', name: 'Alaska Airlines' },
    { code: 'F9', name: 'Frontier Airlines' },
    { code: 'NK', name: 'Spirit Airlines' },
];

const TIME_SLOTS = [
    { value: 'early_morning', label: 'Early Morning', range: '12am - 6am' },
    { value: 'morning', label: 'Morning', range: '6am - 12pm' },
    { value: 'afternoon', label: 'Afternoon', range: '12pm - 6pm' },
    { value: 'evening', label: 'Evening', range: '6pm - 12am' },
];

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'best', label: 'Best' },
    { value: 'price_low', label: 'Cheapest' },
    { value: 'price_high', label: 'Highest Price' },
    { value: 'duration', label: 'Shortest' },
    { value: 'departure', label: 'Departure Time' },
    { value: 'arrival', label: 'Arrival Time' },
];

// ============================================================================
// MOCK DATA GENERATOR
// ============================================================================

const generateMockFlights = (
    origin: string,
    destination: string,
    departDate: string,
    returnDate: string | null,
    adults: number,
    children: number,
    infants: number,
    cabinClass: string
): FlightResult[] => {
    const flights: FlightResult[] = [
        {
            id: 'flight-1',
            airline: 'United Airlines',
            airlineCode: 'UA',
            airlineLogo: 'https://logos-world.net/wp-content/uploads/2021/02/United-Airlines-Logo.png',
            flightNumber: 'UA 1234',
            origin: origin || 'New York',
            originCode: 'JFK',
            destination: destination || 'Los Angeles',
            destinationCode: 'LAX',
            departureTime: '06:00',
            arrivalTime: '09:30',
            duration: '5h 30m',
            durationMinutes: 330,
            stops: 0,
            price: 289,
            currency: 'USD',
            cabinClass: cabinClass,
            seatsLeft: 4,
            aircraft: 'Boeing 737-900',
            amenities: ['wifi', 'power', 'entertainment'],
            baggageIncluded: true,
            refundable: false,
            returnFlight: returnDate ? {
                flightNumber: 'UA 5678',
                departureTime: '18:00',
                arrivalTime: '02:30+1',
                duration: '5h 30m',
                stops: 0,
            } : undefined,
        },
        {
            id: 'flight-2',
            airline: 'Delta Air Lines',
            airlineCode: 'DL',
            airlineLogo: 'https://logos-world.net/wp-content/uploads/2021/02/Delta-Air-Lines-Logo.png',
            flightNumber: 'DL 456',
            origin: origin || 'New York',
            originCode: 'JFK',
            destination: destination || 'Los Angeles',
            destinationCode: 'LAX',
            departureTime: '08:15',
            arrivalTime: '11:45',
            duration: '5h 30m',
            durationMinutes: 330,
            stops: 0,
            price: 312,
            currency: 'USD',
            cabinClass: cabinClass,
            seatsLeft: 7,
            aircraft: 'Airbus A321',
            amenities: ['wifi', 'power', 'entertainment', 'meal'],
            baggageIncluded: true,
            refundable: true,
            returnFlight: returnDate ? {
                flightNumber: 'DL 789',
                departureTime: '20:00',
                arrivalTime: '04:30+1',
                duration: '5h 30m',
                stops: 0,
            } : undefined,
        },
        {
            id: 'flight-3',
            airline: 'American Airlines',
            airlineCode: 'AA',
            airlineLogo: 'https://logos-world.net/wp-content/uploads/2021/02/American-Airlines-Logo.png',
            flightNumber: 'AA 789',
            origin: origin || 'New York',
            originCode: 'JFK',
            destination: destination || 'Los Angeles',
            destinationCode: 'LAX',
            departureTime: '10:30',
            arrivalTime: '15:15',
            duration: '6h 45m',
            durationMinutes: 405,
            stops: 1,
            stopCities: ['Dallas (DFW)'],
            price: 245,
            currency: 'USD',
            cabinClass: cabinClass,
            aircraft: 'Boeing 777-200',
            amenities: ['wifi', 'power'],
            baggageIncluded: true,
            refundable: false,
            returnFlight: returnDate ? {
                flightNumber: 'AA 321',
                departureTime: '16:00',
                arrivalTime: '01:45+1',
                duration: '6h 45m',
                stops: 1,
            } : undefined,
        },
        {
            id: 'flight-4',
            airline: 'JetBlue Airways',
            airlineCode: 'B6',
            airlineLogo: 'https://logos-world.net/wp-content/uploads/2021/02/JetBlue-Logo.png',
            flightNumber: 'B6 1122',
            origin: origin || 'New York',
            originCode: 'JFK',
            destination: destination || 'Los Angeles',
            destinationCode: 'LAX',
            departureTime: '14:00',
            arrivalTime: '17:30',
            duration: '5h 30m',
            durationMinutes: 330,
            stops: 0,
            price: 269,
            currency: 'USD',
            cabinClass: cabinClass,
            seatsLeft: 2,
            aircraft: 'Airbus A320',
            amenities: ['wifi', 'entertainment'],
            baggageIncluded: false,
            refundable: false,
            returnFlight: returnDate ? {
                flightNumber: 'B6 3344',
                departureTime: '22:00',
                arrivalTime: '06:30+1',
                duration: '5h 30m',
                stops: 0,
            } : undefined,
        },
        {
            id: 'flight-5',
            airline: 'Spirit Airlines',
            airlineCode: 'NK',
            airlineLogo: 'https://logos-world.net/wp-content/uploads/2021/02/Spirit-Airlines-Logo.png',
            flightNumber: 'NK 555',
            origin: origin || 'New York',
            originCode: 'LGA',
            destination: destination || 'Los Angeles',
            destinationCode: 'LAX',
            departureTime: '05:45',
            arrivalTime: '12:30',
            duration: '8h 45m',
            durationMinutes: 525,
            stops: 2,
            stopCities: ['Fort Lauderdale (FLL)', 'Denver (DEN)'],
            price: 149,
            currency: 'USD',
            cabinClass: 'economy',
            aircraft: 'Airbus A320neo',
            amenities: [],
            baggageIncluded: false,
            refundable: false,
            returnFlight: returnDate ? {
                flightNumber: 'NK 666',
                departureTime: '13:00',
                arrivalTime: '23:45',
                duration: '8h 45m',
                stops: 2,
            } : undefined,
        },
        {
            id: 'flight-6',
            airline: 'Southwest Airlines',
            airlineCode: 'SW',
            airlineLogo: 'https://logos-world.net/wp-content/uploads/2021/02/Southwest-Airlines-Logo.png',
            flightNumber: 'WN 2233',
            origin: origin || 'New York',
            originCode: 'LGA',
            destination: destination || 'Los Angeles',
            destinationCode: 'LAX',
            departureTime: '12:00',
            arrivalTime: '17:15',
            duration: '7h 15m',
            durationMinutes: 435,
            stops: 1,
            stopCities: ['Phoenix (PHX)'],
            price: 198,
            currency: 'USD',
            cabinClass: 'economy',
            aircraft: 'Boeing 737 MAX 8',
            amenities: ['wifi', 'entertainment'],
            baggageIncluded: true,
            refundable: true,
            returnFlight: returnDate ? {
                flightNumber: 'WN 4455',
                departureTime: '19:00',
                arrivalTime: '03:15+1',
                duration: '7h 15m',
                stops: 1,
            } : undefined,
        },
    ];

    return flights;
};

// ============================================================================
// STEPPER INPUT COMPONENT
// ============================================================================

interface StepperInputProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    label: string;
    sublabel?: string;
    icon?: React.ReactNode;
}

const StepperInput: React.FC<StepperInputProps> = ({
    value,
    onChange,
    min = 0,
    max = 10,
    label,
    sublabel,
    icon,
}) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
                {sublabel && <Typography variant="caption" color="text.secondary">{sublabel}</Typography>}
            </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
                size="small"
                onClick={() => value > min && onChange(value - 1)}
                disabled={value <= min}
                sx={{ border: '1px solid', borderColor: value <= min ? '#e0e0e0' : '#2D6E6A', color: value <= min ? '#e0e0e0' : '#2D6E6A', width: 32, height: 32 }}
            >
                <Remove fontSize="small" />
            </IconButton>
            <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>{value}</Typography>
            <IconButton
                size="small"
                onClick={() => value < max && onChange(value + 1)}
                disabled={value >= max}
                sx={{ border: '1px solid', borderColor: value >= max ? '#e0e0e0' : '#2D6E6A', color: value >= max ? '#e0e0e0' : '#2D6E6A', width: 32, height: 32 }}
            >
                <Add fontSize="small" />
            </IconButton>
        </Box>
    </Box>
);

// ============================================================================
// FLIGHT CARD COMPONENT
// ============================================================================

interface FlightCardProps {
    flight: FlightResult;
    isRoundTrip: boolean;
    totalTravelers: number;
    onSelect: (flight: FlightResult) => void;
}

const FlightCard: React.FC<FlightCardProps> = ({ flight, isRoundTrip, totalTravelers, onSelect }) => {
    const amenityIcons: Record<string, React.ReactElement> = {
        wifi: <Wifi sx={{ fontSize: 16 }} />,
        power: <Power sx={{ fontSize: 16 }} />,
        entertainment: <EventSeat sx={{ fontSize: 16 }} />,
        meal: <Restaurant sx={{ fontSize: 16 }} />,
    };

    return (
        <Card
            sx={{
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                transition: 'box-shadow 0.2s, border-color 0.2s',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderColor: '#2D6E6A' },
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    {/* Flight Details */}
                    <Box sx={{ flex: 1 }}>
                        {/* Outbound Flight */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isRoundTrip ? 2 : 0 }}>
                            {/* Airline */}
                            <Box sx={{ minWidth: 80, textAlign: 'center' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{flight.airlineCode}</Typography>
                                <Typography variant="caption" color="text.secondary">{flight.flightNumber}</Typography>
                            </Box>

                            {/* Times */}
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{flight.departureTime}</Typography>
                                    <Typography variant="caption" color="text.secondary">{flight.originCode}</Typography>
                                </Box>

                                <Box sx={{ flex: 1, textAlign: 'center', px: 2 }}>
                                    <Typography variant="caption" color="text.secondary">{flight.duration}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Box sx={{ flex: 1, height: 2, bgcolor: '#e0e0e0' }} />
                                        {flight.stops === 0 ? (
                                            <Flight sx={{ mx: 1, color: '#2D6E6A', transform: 'rotate(90deg)' }} />
                                        ) : (
                                            <Box sx={{ mx: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {Array.from({ length: flight.stops }).map((_, i) => (
                                                    <Box key={i} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FF9800' }} />
                                                ))}
                                            </Box>
                                        )}
                                        <Box sx={{ flex: 1, height: 2, bgcolor: '#e0e0e0' }} />
                                    </Box>
                                    <Typography variant="caption" color={flight.stops === 0 ? 'success.main' : 'text.secondary'}>
                                        {flight.stops === 0 ? 'Nonstop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                                    </Typography>
                                    {flight.stopCities && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {flight.stopCities.join(', ')}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{flight.arrivalTime}</Typography>
                                    <Typography variant="caption" color="text.secondary">{flight.destinationCode}</Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Return Flight */}
                        {isRoundTrip && flight.returnFlight && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 2, borderTop: '1px dashed #e0e0e0' }}>
                                <Box sx={{ minWidth: 80, textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{flight.airlineCode}</Typography>
                                    <Typography variant="caption" color="text.secondary">{flight.returnFlight.flightNumber}</Typography>
                                </Box>

                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{flight.returnFlight.departureTime}</Typography>
                                        <Typography variant="caption" color="text.secondary">{flight.destinationCode}</Typography>
                                    </Box>

                                    <Box sx={{ flex: 1, textAlign: 'center', px: 2 }}>
                                        <Typography variant="caption" color="text.secondary">{flight.returnFlight.duration}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Box sx={{ flex: 1, height: 2, bgcolor: '#e0e0e0' }} />
                                            <Flight sx={{ mx: 1, color: '#2D6E6A', transform: 'rotate(90deg)' }} />
                                            <Box sx={{ flex: 1, height: 2, bgcolor: '#e0e0e0' }} />
                                        </Box>
                                        <Typography variant="caption" color={flight.returnFlight.stops === 0 ? 'success.main' : 'text.secondary'}>
                                            {flight.returnFlight.stops === 0 ? 'Nonstop' : `${flight.returnFlight.stops} stop${flight.returnFlight.stops > 1 ? 's' : ''}`}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{flight.returnFlight.arrivalTime}</Typography>
                                        <Typography variant="caption" color="text.secondary">{flight.originCode}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Amenities & Info */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                            {flight.amenities.map((amenity) => (
                                <Chip
                                    key={amenity}
                                    icon={amenityIcons[amenity]}
                                    label={amenity}
                                    size="small"
                                    sx={{ height: 24, fontSize: '0.7rem', textTransform: 'capitalize' }}
                                />
                            ))}
                            {flight.baggageIncluded && (
                                <Chip icon={<Luggage sx={{ fontSize: 16 }} />} label="Baggage included" size="small" sx={{ height: 24, fontSize: '0.7rem', bgcolor: '#E8F5E9', color: '#2E7D32' }} />
                            )}
                            {flight.refundable && (
                                <Chip label="Refundable" size="small" sx={{ height: 24, fontSize: '0.7rem', bgcolor: '#E3F2FD', color: '#1976D2' }} />
                            )}
                        </Box>
                    </Box>

                    {/* Price & Book */}
                    <Box sx={{ minWidth: 140, textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                ${flight.price * totalTravelers}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {totalTravelers > 1 ? `$${flight.price}/person` : 'per person'}
                            </Typography>
                            {isRoundTrip && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    round trip
                                </Typography>
                            )}
                            {flight.seatsLeft && flight.seatsLeft <= 5 && (
                                <Typography variant="caption" sx={{ color: '#F44336', fontWeight: 600, display: 'block' }}>
                                    {flight.seatsLeft} seats left
                                </Typography>
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            onClick={() => onSelect(flight)}
                            sx={{
                                mt: 2,
                                bgcolor: '#2D6E6A',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': { bgcolor: '#009688' },
                            }}
                        >
                            Select
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// ============================================================================
// FILTER SIDEBAR COMPONENT
// ============================================================================

interface FilterSidebarProps {
    filters: SearchFilters;
    onChange: (filters: SearchFilters) => void;
    maxPrice: number;
    resultCount: number;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ filters, onChange, maxPrice, resultCount }) => {
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        stops: true,
        price: true,
        airlines: true,
        times: false,
        amenities: false,
    });

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const updateFilter = <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
        onChange({ ...filters, [key]: value });
    };

    const toggleArrayFilter = (key: 'stops' | 'airlines' | 'departureTime' | 'arrivalTime', value: string | number) => {
        const current = filters[key] as (string | number)[];
        const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        updateFilter(key, updated as any);
    };

    const FilterSection: React.FC<{ title: string; id: string; children: React.ReactNode }> = ({ title, id, children }) => (
        <Box sx={{ mb: 2 }}>
            <Box onClick={() => toggleSection(id)} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', py: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
                {expandedSections[id] ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={expandedSections[id]}><Box sx={{ pt: 1 }}>{children}</Box></Collapse>
            <Divider sx={{ mt: 2 }} />
        </Box>
    );

    return (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList /> Filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{resultCount} flights found</Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Stops */}
            <FilterSection title="Stops" id="stops">
                <FormGroup>
                    {[0, 1, 2].map((stops) => (
                        <FormControlLabel
                            key={stops}
                            control={<Checkbox checked={filters.stops.includes(stops)} onChange={() => toggleArrayFilter('stops', stops)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                            label={stops === 0 ? 'Nonstop' : stops === 1 ? '1 stop' : '2+ stops'}
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Price */}
            <FilterSection title="Price" id="price">
                <Slider
                    value={filters.priceRange}
                    onChange={(_, value) => updateFilter('priceRange', value as [number, number])}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `$${v}`}
                    min={0}
                    max={maxPrice}
                    sx={{ color: '#2D6E6A' }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">${filters.priceRange[0]}</Typography>
                    <Typography variant="caption">${filters.priceRange[1]}</Typography>
                </Box>
            </FilterSection>

            {/* Airlines */}
            <FilterSection title="Airlines" id="airlines">
                <FormGroup>
                    {AIRLINES.map((airline) => (
                        <FormControlLabel
                            key={airline.code}
                            control={<Checkbox checked={filters.airlines.includes(airline.code)} onChange={() => toggleArrayFilter('airlines', airline.code)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                            label={airline.name}
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Departure Time */}
            <FilterSection title="Departure Time" id="times">
                <FormGroup>
                    {TIME_SLOTS.map((slot) => (
                        <FormControlLabel
                            key={slot.value}
                            control={<Checkbox checked={filters.departureTime.includes(slot.value)} onChange={() => toggleArrayFilter('departureTime', slot.value)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                            label={<Box><Typography variant="body2">{slot.label}</Typography><Typography variant="caption" color="text.secondary">{slot.range}</Typography></Box>}
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Amenities */}
            <FilterSection title="Amenities" id="amenities">
                <FormControlLabel
                    control={<Checkbox checked={filters.baggageIncluded} onChange={(e) => updateFilter('baggageIncluded', e.target.checked)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                    label="Baggage included"
                />
                <FormControlLabel
                    control={<Checkbox checked={filters.refundable} onChange={(e) => updateFilter('refundable', e.target.checked)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                    label="Refundable"
                />
            </FilterSection>
        </Paper>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FlightSearchResultsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Parse URL params
    const tripType = (searchParams.get('tripType') as 'roundtrip' | 'oneway') || 'roundtrip';
    const initialOrigin = searchParams.get('origin') || '';
    const initialDestination = searchParams.get('destination') || '';
    const initialDepartDate = searchParams.get('departDate') || '';
    const initialReturnDate = searchParams.get('returnDate') || '';
    const initialAdults = parseInt(searchParams.get('adults') || '1', 10);
    const initialChildren = parseInt(searchParams.get('children') || '0', 10);
    const initialInfants = parseInt(searchParams.get('infants') || '0', 10);
    const initialCabinClass = searchParams.get('cabinClass') || 'economy';

    // Search state
    const [origin, setOrigin] = useState(initialOrigin);
    const [destination, setDestination] = useState(initialDestination);
    const [departDate, setDepartDate] = useState(initialDepartDate);
    const [returnDate, setReturnDate] = useState(initialReturnDate);
    const [adults, setAdults] = useState(initialAdults);
    const [children, setChildren] = useState(initialChildren);
    const [infants, setInfants] = useState(initialInfants);
    const [cabinClass, setCabinClass] = useState(initialCabinClass);

    // Results state
    const [loading, setLoading] = useState(true);
    const [flights, setFlights] = useState<FlightResult[]>([]);

    // Filter state
    const [filters, setFilters] = useState<SearchFilters>({
        priceRange: [0, 1000],
        stops: [],
        airlines: [],
        departureTime: [],
        arrivalTime: [],
        refundable: false,
        baggageIncluded: false,
    });

    // Sort state
    const [sortBy, setSortBy] = useState<SortOption>('best');

    // Mobile filter drawer
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // Search form expanded
    const [searchExpanded, setSearchExpanded] = useState(false);

    // Total travelers
    const totalTravelers = adults + children + infants;
    const isRoundTrip = tripType === 'roundtrip';

    // Load results
    useEffect(() => {
        const loadResults = async () => {
            setLoading(true);
            await new Promise((resolve) => setTimeout(resolve, 1200));

            if (initialOrigin && initialDestination) {
                const mockFlights = generateMockFlights(
                    initialOrigin,
                    initialDestination,
                    initialDepartDate,
                    isRoundTrip ? initialReturnDate : null,
                    initialAdults,
                    initialChildren,
                    initialInfants,
                    initialCabinClass
                );
                setFlights(mockFlights);
                const maxPrice = Math.max(...mockFlights.map((f) => f.price), 1000);
                setFilters((prev) => ({ ...prev, priceRange: [0, maxPrice] }));
            }
            setLoading(false);
        };
        loadResults();
    }, [initialOrigin, initialDestination, initialDepartDate, initialReturnDate, initialAdults, initialChildren, initialInfants, initialCabinClass, isRoundTrip]);

    // Filter and sort
    const filteredFlights = useMemo(() => {
        let result = [...flights];

        result = result.filter((flight) => {
            if (flight.price < filters.priceRange[0] || flight.price > filters.priceRange[1]) return false;
            if (filters.stops.length > 0 && !filters.stops.includes(flight.stops > 1 ? 2 : flight.stops)) return false;
            if (filters.airlines.length > 0 && !filters.airlines.includes(flight.airlineCode)) return false;
            if (filters.refundable && !flight.refundable) return false;
            if (filters.baggageIncluded && !flight.baggageIncluded) return false;
            return true;
        });

        switch (sortBy) {
            case 'price_low': result.sort((a, b) => a.price - b.price); break;
            case 'price_high': result.sort((a, b) => b.price - a.price); break;
            case 'duration': result.sort((a, b) => a.durationMinutes - b.durationMinutes); break;
            case 'departure': result.sort((a, b) => a.departureTime.localeCompare(b.departureTime)); break;
            case 'arrival': result.sort((a, b) => a.arrivalTime.localeCompare(b.arrivalTime)); break;
            default: result.sort((a, b) => (a.stops - b.stops) || (a.price - b.price));
        }

        return result;
    }, [flights, filters, sortBy]);

    // Handle search
    const handleSearch = () => {
        const params = new URLSearchParams({
            tripType,
            origin,
            destination,
            departDate,
            ...(isRoundTrip && { returnDate }),
            adults: adults.toString(),
            children: children.toString(),
            infants: infants.toString(),
            cabinClass,
        });
        setSearchParams(params);
        setSearchExpanded(false);
    };

    // Handle flight selection
    const handleSelectFlight = (flight: FlightResult) => {
        const params = new URLSearchParams({
            type: 'flight',
            flightId: flight.id,
            flightName: `${flight.airline} ${flight.flightNumber}`,
            origin,
            destination,
            departDate,
            ...(isRoundTrip && { returnDate }),
            travelers: totalTravelers.toString(),
            pricePerPerson: flight.price.toString(),
            totalPrice: (flight.price * totalTravelers).toString(),
        });
        navigate(`/book/checkout?${params.toString()}`);
    };

    const today = todayLocalString();

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F5F5F5' }}>
            {/* Header */}
            <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, zIndex: 100 }}>
                <Container maxWidth="xl">
                    <Box sx={{ py: 1 }}>
                        <Breadcrumbs sx={{ fontSize: '0.85rem' }}>
                            <Link component="button" onClick={() => navigate('/')} underline="hover" color="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Home sx={{ fontSize: 16 }} /> Home
                            </Link>
                            <Link component="button" onClick={() => navigate(-1)} underline="hover" color="inherit">Flights</Link>
                            <Typography color="text.primary" sx={{ fontSize: '0.85rem' }}>{origin} → {destination}</Typography>
                        </Breadcrumbs>
                    </Box>

                    {/* Search Summary */}
                    <Box sx={{ py: 2 }}>
                        <Paper
                            onClick={() => setSearchExpanded(!searchExpanded)}
                            sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderRadius: 2, border: '1px solid #e0e0e0', '&:hover': { borderColor: '#2D6E6A' } }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <FlightTakeoff sx={{ color: '#2D6E6A' }} />
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{origin || 'From'}</Typography>
                                    <ArrowRightAlt />
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{destination || 'To'}</Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant="body2">
                                    {departDate ? formatDate(departDate, { month: 'short', day: 'numeric' }) : 'Depart'}
                                    {isRoundTrip && returnDate && ` - ${formatDate(returnDate, { month: 'short', day: 'numeric' })}`}
                                </Typography>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant="body2">{totalTravelers} traveler{totalTravelers > 1 ? 's' : ''}</Typography>
                            </Box>
                            <Button variant="contained" sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}>
                                Modify Search
                            </Button>
                        </Paper>
                    </Box>
                </Container>
            </Box>

            {/* Main Content */}
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Grid container spacing={3}>
                    {/* Filter Sidebar */}
                    {!isMobile && (
                        <Grid item md={3}>
                            <FilterSidebar filters={filters} onChange={setFilters} maxPrice={Math.max(...flights.map((f) => f.price), 1000)} resultCount={filteredFlights.length} />
                        </Grid>
                    )}

                    {/* Results */}
                    <Grid item xs={12} md={9}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {origin} → {destination}: {filteredFlights.length} flights
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isMobile && (
                                    <Button variant="outlined" startIcon={<TuneRounded />} onClick={() => setFilterDrawerOpen(true)} sx={{ borderRadius: 2, textTransform: 'none' }}>
                                        Filters
                                    </Button>
                                )}
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <InputLabel>Sort by</InputLabel>
                                    <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value as SortOption)} sx={{ borderRadius: 2 }}>
                                        {SORT_OPTIONS.map((opt) => (<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {loading ? (
                            <Box>{[1, 2, 3].map((i) => (<Skeleton key={i} variant="rounded" height={180} sx={{ mb: 2, borderRadius: 2 }} />))}</Box>
                        ) : filteredFlights.length === 0 ? (
                            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                                <Flight sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>No flights found</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Try adjusting your filters or search criteria</Typography>
                            </Paper>
                        ) : (
                            <Box>{filteredFlights.map((flight) => (<FlightCard key={flight.id} flight={flight} isRoundTrip={isRoundTrip} totalTravelers={totalTravelers} onSelect={handleSelectFlight} />))}</Box>
                        )}
                    </Grid>
                </Grid>
            </Container>

            {/* Mobile Filter Drawer */}
            <Drawer anchor="bottom" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)} PaperProps={{ sx: { maxHeight: '85vh', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Filters</Typography>
                        <IconButton onClick={() => setFilterDrawerOpen(false)}><Close /></IconButton>
                    </Box>
                    <FilterSidebar filters={filters} onChange={setFilters} maxPrice={Math.max(...flights.map((f) => f.price), 1000)} resultCount={filteredFlights.length} />
                    <Button fullWidth variant="contained" onClick={() => setFilterDrawerOpen(false)} sx={{ mt: 2, bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}>
                        Show {filteredFlights.length} Results
                    </Button>
                </Box>
            </Drawer>
        </Box>
    );
};

export default FlightSearchResultsPage;
